import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Users, RefreshCw, ChevronRight, ChevronDown, Mail, Phone,
  Crown, Shield, Wrench, Search, UserCog, Briefcase
} from "lucide-react";

// =============================================================================
// TEAM (mirror from surveydrafts.us)
// Vendor → Role bucket → Members tree, with quick stats per vendor.
// Local-user CRUD has moved to /users (admin-only) — link is in the header.
// =============================================================================
const ROLE_ICONS = {
  fieldcrew:    Wrench,
  drafter:      UserCog,
  qaqc:         Shield,
  finalqc:      Shield,
  team_manager: Crown,
  admin:        Crown,
  vendor:       Briefcase,
};
const ROLE_COLORS = {
  fieldcrew:    "bg-sky-100",
  drafter:      "bg-yellow-100",
  qaqc:         "bg-indigo-100",
  finalqc:      "bg-purple-100",
  team_manager: "bg-emerald-100",
  admin:        "bg-rose-100",
  vendor:       "bg-amber-100",
};

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState({});      // { [vendor_id]: true|false }
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/integrations/surveydrafts/hierarchy")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onSyncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/jobs/sync-surveydrafts-extended");
      if (data.ok === false) {
        toast.error(data.reason || "Sync failed");
      } else {
        toast.success("Team data refreshed from surveydrafts.us");
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed. Set X-API-Key on the Jobs board first.");
    }
    setSyncing(false);
  };

  const toggle = (vid) => setExpanded((m) => ({ ...m, [vid]: !m[vid] }));

  const filtered = useMemo(() => {
    if (!data) return null;
    const s = q.trim().toLowerCase();
    if (!s) return data.vendors;
    return data.vendors.map((v) => ({
      ...v,
      roles: v.roles
        .map((r) => ({
          ...r,
          members: r.members.filter((m) =>
            (m.name || "").toLowerCase().includes(s) ||
            (m.email || "").toLowerCase().includes(s) ||
            (r.label || "").toLowerCase().includes(s),
          ),
        }))
        .filter((r) => r.members.length > 0),
    })).filter((v) => v.roles.length > 0 || (v.vendor?.name || "").toLowerCase().includes(s));
  }, [data, q]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="team-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">SURVEYDRAFTS.US · LIVE MIRROR</div>
          <h1 className="font-heading text-4xl font-black tracking-tight leading-none mt-1">Members</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Individual team members mirrored from surveydrafts.us · grouped by team &amp; role
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search members…"
              className="brut-input pl-7 pr-3 py-2 text-xs font-mono w-56"
              data-testid="team-search-input"
            />
          </div>
          {isAdmin && (
            <Link
              to="/users"
              className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1"
              data-testid="team-go-users"
              title="Manage Land Drafters login accounts (admin-only)"
            >
              <UserCog className="w-3.5 h-3.5" /> LOCAL USERS
            </Link>
          )}
          <button
            onClick={onSyncNow}
            disabled={syncing}
            className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-emerald-300 hover:bg-emerald-400 disabled:opacity-60"
            data-testid="team-sync-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "SYNCING…" : "SYNC NOW"}
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="team-summary">
          <SummaryTile label="TEAMS" value={data.totals.vendors} testid="team-tile-vendors" />
          <SummaryTile label="MEMBERS" value={data.totals.members} testid="team-tile-members" />
          <SummaryTile label="ROLE BUCKETS" value={data.workflow_roles.length} testid="team-tile-roles" />
          <SummaryTile
            label="LARGEST TEAM"
            value={(() => {
              const real = data.vendors.filter((v) => v.vendor?.surveydrafts_id);
              const top = [...real].sort((a, b) => b.members_total - a.members_total)[0];
              return top ? `${top.members_total} (${(top.vendor.name || "").slice(0, 16)})` : "—";
            })()}
            testid="team-tile-largest"
          />
        </div>
      )}

      {loading && <div className="font-mono text-xs text-zinc-500">Loading team hierarchy…</div>}

      {!loading && filtered && filtered.length === 0 && (
        <div className="brut-card p-10 text-center text-zinc-500 font-mono text-sm">
          NO MATCHES — TRY A DIFFERENT SEARCH OR CLICK <strong>SYNC NOW</strong>.
        </div>
      )}

      {filtered && filtered.map((v) => (
        <VendorBranch
          key={v.vendor.surveydrafts_id || v.vendor.name}
          v={v}
          expanded={expanded[v.vendor.surveydrafts_id || v.vendor.name] !== false}
          onToggle={() => toggle(v.vendor.surveydrafts_id || v.vendor.name)}
        />
      ))}
    </div>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <div className="brut-card p-4" data-testid={testid}>
      <div className="label-mono text-zinc-500">{label}</div>
      <div className="font-heading text-3xl font-black tracking-tight mt-1 truncate">{value}</div>
    </div>
  );
}

function VendorBranch({ v, expanded, onToggle }) {
  const ven = v.vendor;
  const visibleRoles = v.roles.filter((r) => r.count > 0);
  return (
    <div className="brut-card overflow-hidden" data-testid={`team-vendor-${ven.surveydrafts_id || ven.name}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-yellow-50 transition-colors text-left"
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-black truncate">{ven.name || "Unnamed vendor"}</div>
          {ven.company && ven.company !== ven.name && (
            <div className="text-xs text-zinc-600 font-mono truncate">{ven.company}</div>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs font-mono">
          <span className="px-2 py-1 border border-zinc-900 bg-zinc-50">
            {v.members_total} MEMBERS
          </span>
          <span className="hidden md:inline-flex px-2 py-1 border border-zinc-900 bg-emerald-50">
            {Object.values(v.stage_counts || {}).reduce((a, c) => a + c, 0)} jobs
          </span>
          {ven.surveydrafts_id && (
            <Link
              to={`/jobs?vendor_id=${encodeURIComponent(ven.surveydrafts_id)}&vendor_name=${encodeURIComponent(ven.name || "")}`}
              className="brut-btn-secondary px-2 py-1 text-[10px]"
              onClick={(e) => e.stopPropagation()}
              data-testid={`team-vendor-jobs-${ven.surveydrafts_id}`}
            >
              VIEW JOBS
            </Link>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t-2 border-zinc-900 bg-zinc-50 divide-y divide-zinc-200">
          {visibleRoles.length === 0 && (
            <div className="p-6 text-center text-xs font-mono text-zinc-500">
              No members assigned to this vendor on surveydrafts.us.
            </div>
          )}
          {visibleRoles.map((r) => <RoleBucket key={r.key} r={r} />)}
        </div>
      )}
    </div>
  );
}

function RoleBucket({ r }) {
  const Icon = ROLE_ICONS[r.key] || Users;
  const tone = ROLE_COLORS[r.key] || "bg-zinc-100";
  return (
    <div className="p-4" data-testid={`role-bucket-${r.key}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-7 h-7 border border-zinc-900 ${tone} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <div className="label-mono">{r.label}</div>
        <span className="text-xs font-mono text-zinc-500">· {r.count}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {r.members.map((m) => (
          <div
            key={m.id}
            className="border border-zinc-900 bg-white p-3"
            data-testid={`team-member-${m.surveydrafts_id || m.id}`}
          >
            <div className="font-bold text-sm truncate" title={m.name}>{m.name || "—"}</div>
            {m.email && (
              <div className="flex items-center gap-1 text-[10px] font-mono text-zinc-600 mt-1 truncate" title={m.email}>
                <Mail className="w-3 h-3" /> {m.email}
              </div>
            )}
            {m.phone && (
              <div className="flex items-center gap-1 text-[10px] font-mono text-zinc-500 mt-0.5">
                <Phone className="w-3 h-3" /> {m.phone}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
